import * as React from "react";

interface UseUploadEstimateParams {
  uploadedBytes: number;
  activeBytes: number;
  totalBytes: number;
}

interface UseUploadEstimateResult {
  etaSeconds: number;
  estimatedCompletionTime: Date | null;
  smoothedSpeed: number;
}

export function useUploadEstimate({
  uploadedBytes,
  activeBytes,
  totalBytes,
}: UseUploadEstimateParams): UseUploadEstimateResult {
  const [smoothedSpeed, setSmoothedSpeed] = React.useState(0);
  const lastUpdateTimeRef = React.useRef(Date.now());
  const startTimeRef = React.useRef(Date.now());
  const startBytesRef = React.useRef(0);

  const totalUploadedBytes = uploadedBytes + activeBytes;

  // Calculate current speed based on total bytes over time
  const currentSpeed = React.useMemo(() => {
    const now = Date.now();
    const elapsedSeconds = (now - startTimeRef.current) / 1000;

    // Wait at least 1 second before calculating speed to avoid division by zero
    if (elapsedSeconds < 1) return 0;

    const bytesUploaded = totalUploadedBytes - startBytesRef.current;
    return bytesUploaded / elapsedSeconds;
  }, [totalUploadedBytes]);

  // Reset timing when upload starts (when bytes go from 0 to > 0)
  React.useEffect(() => {
    if (totalUploadedBytes > 0 && startBytesRef.current === 0) {
      startTimeRef.current = Date.now();
      startBytesRef.current = totalUploadedBytes;
    }
  }, [totalUploadedBytes]);

  // Throttle smoothed speed updates to every 2 seconds
  React.useEffect(() => {
    const now = Date.now();
    const timeSinceLastUpdate = (now - lastUpdateTimeRef.current) / 1000;

    if (timeSinceLastUpdate >= 2) {
      setSmoothedSpeed((prevSmoothedSpeed) => {
        if (prevSmoothedSpeed === 0 && currentSpeed > 0) {
          return currentSpeed;
        } else if (currentSpeed > 0) {
          const alpha = 0.3;
          return alpha * currentSpeed + (1 - alpha) * prevSmoothedSpeed;
        }
        return prevSmoothedSpeed;
      });
      lastUpdateTimeRef.current = now;
    }
  }, [currentSpeed]);

  const remainingBytes = totalBytes - totalUploadedBytes;
  const etaSeconds = smoothedSpeed > 0 ? remainingBytes / smoothedSpeed : 0;

  const estimatedCompletionTime =
    etaSeconds > 0 ? new Date(Date.now() + etaSeconds * 1000) : null;

  return {
    etaSeconds,
    estimatedCompletionTime,
    smoothedSpeed,
  };
}
